import React, { useState, useEffect } from 'react';
import { Title } from '../common/styled';
import ToastMessage from '../../components/ToastMessage';
import RolloverDataGrid from './components/RolloverDataGrid';
import { generateColumns } from './utils/columnUtils';
import SearchFilters from './components/SearchFilters';
import { defaultAcademicYear } from '../../constants/academicYears';
import { RolloverDashboardReport } from './types';
import { SnackbarSeverityType } from '../../enums';
import report from '../../apis/report';
import { isEmpty } from 'lodash';
import { downloadReport } from '../../utils/reportUtils';
import { PageContainer } from './styled';
import { transformRolloverDashboardReportData } from './utils/transformRolloverDashboardReportData'; // Import transform function

interface RolloverDashboardPageProps {
  getGradeWiseSummary?: boolean;
}

const RolloverDashboardPage: React.FC<RolloverDashboardPageProps> = ({
  getGradeWiseSummary = false
}) => {
  const [academicYear, setAcademicYear] = useState(defaultAcademicYear);
  const [loading, setLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [reportData, setReportData] = useState<RolloverDashboardReport[]>([]);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );

  useEffect(() => {
    setReportData([]);
  }, [academicYear]);

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await report.fetchRolloverDashboardReport({
        academicYear,
        getGradeWiseSummary
      });
      setReportData(response);
    } catch (e: any) {
      console.error(e);
      showToast(
        `Failed to fetch report. Error: ${e?.message || ''}`,
        SnackbarSeverityType.error
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    setIsDownloading(true);
    try {
      let dataToDownload = reportData;
      if (isEmpty(dataToDownload)) {
        dataToDownload = await report.fetchRolloverDashboardReport({
          academicYear,
          getGradeWiseSummary
        });
      }

      if (isEmpty(dataToDownload)) {
        showToast(
          'There is no data available to download as an Excel file.',
          SnackbarSeverityType.error
        );
        return;
      }

      const transformedData =
        transformRolloverDashboardReportData(dataToDownload);

      downloadReport(
        transformedData,
        'School-Rollover-Status-Report',
        'School Rollover Dashboard Report',
        showToast
      );
    } catch (e: any) {
      console.error(e);
      showToast(
        `Failed to download report. Error: ${e?.message || ''}`,
        SnackbarSeverityType.error
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const columns = generateColumns();

  return (
    <PageContainer>
      <Title>
        Rollover Dashboard
        {getGradeWiseSummary && ' (School And GradeWise)'}
      </Title>
      <SearchFilters
        academicYear={academicYear}
        loading={loading}
        isDownloading={isDownloading}
        onAcademicYearChange={setAcademicYear}
        onSearch={fetchReport}
        onDownload={handleDownloadReport}
      />
      {reportData.length > 0 && (
        <RolloverDataGrid
          reportData={reportData}
          columns={columns}
          loading={loading}
          getGradeWiseSummary={getGradeWiseSummary}
        />
      )}
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </PageContainer>
  );
};

export default RolloverDashboardPage;
